package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.groups.asGroupFailure
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.GroupFailure
import com.swetdot.swetgram.domain.GroupId
import com.swetdot.swetgram.domain.GroupInviteLink
import com.swetdot.swetgram.domain.GroupVisibility
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/**
 * SPS §12.3's invite link, which is two sentences of spec and a great deal of consequence.
 *
 * The folder says only "Groups support invite links. Invite links allow communities to migrate from
 * other platforms." There is no screen in SDS §14, no states, and no rotation semantics anywhere.
 * STA §9.2 adds three rules -- random tokens, owner/admin may rotate, and "invite token can be
 * disabled" -- and the last of those the frozen contract does not have: THERE IS NO DISABLE, DELETE
 * OR REVOKE OPERATION in v1.0.0. So everything below is either the contract's shape or a labelled
 * DECISION, and the one thing this screen does not do is offer a control for an endpoint.
 *
 *
 * THE ONE FACT THIS WHOLE SCREEN IS BUILT AROUND
 *
 * `createOrRotateInviteLink` is the only operation that returns a link, and it MUTATES. There is no
 * `GET /groups/{id}/invite-link`. For an Owner or an Admin, asking to see the link REPLACES it, and
 * every link that community has already pasted into three other apps stops working -- irreversibly,
 * since nothing restores an old token.
 *
 * Two consequences, and both are load-bearing:
 *
 *  1. THIS VIEW MODEL NEVER FETCHES A LINK ON ITS OWN. [refresh] reads the GROUP -- for the name,
 *     the visibility and the permissions -- and nothing else. A link only ever arrives from an
 *     explicit tap whose copy said what the tap would do. An `init { }` that loaded the link would
 *     mean that opening this screen to look at it destroyed it.
 *  2. THE FIRST TAP IS AMBIGUOUS AND IS PRESENTED AS SUCH. With no read operation the client cannot
 *     know whether the group already has a link, so for a rotator the first call is "create, or
 *     replace whatever is there". [InviteLinkAction.CREATE_OR_REPLACE] is that state, and it is
 *     confirmed for exactly this reason. Only once a link is on screen can the client say for
 *     certain that the next call destroys something ([InviteLinkAction.REPLACE]).
 *
 * A public group's plain member is the exception that makes this bearable: DECISIONS §7 has the
 * server return the CURRENT link to them without writing, because "all members may share the
 * invitation" is what `invite_permission` means. For them the call is a read in everything but the
 * verb, and [InviteLinkAction.SHOW] says so.
 */

// ---------------------------------------------------------------------------------------------
// The rules -- pure, so they can be tested without a repository
// ---------------------------------------------------------------------------------------------

/**
 * The single control this screen offers, and what pressing it will actually do.
 *
 * One enum rather than a pair of booleans on the state, because the label, the confirmation and the
 * button's own style all have to agree with each other -- and the failure mode of getting that wrong
 * is a user pressing "Show link" and silently invalidating the one on the poster.
 */
internal enum class InviteLinkAction {
    /**
     * A public group's plain member. The call returns the group's current link and writes nothing,
     * so it needs no confirmation -- there is nothing to lose by pressing it.
     */
    SHOW,

    /**
     * Owner or Admin with nothing on screen yet. The call either mints the group's first link or
     * replaces one that already exists, and the CLIENT CANNOT TELL WHICH, because no operation
     * reads a link without writing one. Confirmed on that basis: the user is agreeing to the worse
     * of the two readings.
     */
    CREATE_OR_REPLACE,

    /**
     * Owner or Admin with a link on screen. The next call definitely destroys THIS link. Confirmed,
     * and the confirmation can finally be specific.
     */
    REPLACE,

    /** Nothing to offer: the group is not loaded, or this viewer may not share its invitation. */
    NONE,
}

/**
 * Which control to draw, given the group and whether a link is currently on screen.
 *
 * `canInviteMembers` gates the SCREEN and `canRotateInviteLink` gates the DESTRUCTIVE half, which
 * is the split DECISIONS §7 makes for one endpoint with two meanings. A public group's plain member
 * therefore gets [SHOW] and, once they have the link, nothing further: rotation would be
 * `403 insufficient_group_role`, and re-reading is what leaving and reopening the screen does.
 */
internal fun inviteLinkAction(group: Group?, hasLink: Boolean): InviteLinkAction = when {
    group == null -> InviteLinkAction.NONE
    !group.permissions.canInviteMembers -> InviteLinkAction.NONE
    !group.permissions.canRotateInviteLink ->
        if (hasLink) InviteLinkAction.NONE else InviteLinkAction.SHOW
    hasLink -> InviteLinkAction.REPLACE
    else -> InviteLinkAction.CREATE_OR_REPLACE
}

/**
 * What the link does for whoever opens it -- the sentence above the token.
 *
 * The difference is the entire product difference between the two kinds of group and it is not
 * guessable from the URL: a public group's link admits somebody on the spot, a private group's link
 * only lets them ASK. Somebody about to paste this into a channel of two thousand people needs to
 * know which of those they are doing.
 */
// TODO(localization): every string in this file moves into resources for EN + RU.
internal fun inviteLinkAudienceLine(visibility: GroupVisibility): String = when (visibility) {
    GroupVisibility.PUBLIC ->
        "Anyone who opens this link joins the group straight away."
    GroupVisibility.PRIVATE ->
        "Anyone who opens this link can ask to join. An admin has to approve them."
}

/**
 * The warning attached to the control, or null when there is nothing to warn about.
 *
 * [InviteLinkAction.SHOW] gets none because it writes nothing. The other two get the same fact in
 * different words, and the difference matters: one of them is certain and the other is not.
 */
internal fun inviteLinkCaution(action: InviteLinkAction): String? = when (action) {
    InviteLinkAction.SHOW, InviteLinkAction.NONE -> null
    InviteLinkAction.CREATE_OR_REPLACE ->
        "A group has one invite link at a time. If this group already has one, creating a link " +
            "now stops the old one working, including anywhere it has already been shared."
    InviteLinkAction.REPLACE ->
        "Replacing the link stops this one working immediately, everywhere it has been shared. " +
            "There is no way to bring it back."
}

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

data class GroupInviteUiState(
    /** Read on entry, for the name, the visibility and the two permissions. Never the link. */
    val group: Group? = null,
    /**
     * The link, and ONLY ever one this session actually received.
     *
     * Null on entry and null again the instant a replacement is asked for. Never restored from
     * anywhere: there is nothing to restore it from, and a token remembered across a rotation is
     * precisely the stale credential this screen exists to avoid showing.
     */
    val link: GroupInviteLink? = null,
    val loadedOnce: Boolean = false,
    /** A create or replace is in flight. */
    val busy: Boolean = false,
    /**
     * A rotation failed in a way that leaves the client unable to say which link is live.
     *
     * The request is never retried (no idempotency key), so a timeout or a dropped connection can
     * leave the server having committed a new token that this client never saw. The old link may
     * therefore be dead with nothing on screen to replace it. Saying "something went wrong" would
     * imply nothing happened, which is the one thing that cannot be promised.
     */
    val uncertain: Boolean = false,
    val error: String? = null,
) {
    /** The single control, derived rather than stored so it cannot disagree with [link]. */
    internal val action: InviteLinkAction get() = inviteLinkAction(group, link != null)

    /** True while the screen has nothing to show and no reason yet to say so. */
    val loading: Boolean get() = group == null && !loadedOnce
}

/**
 * SPS §12.3. One group's invite link: how to get one, how to share it, and how to kill it.
 */
class GroupInviteViewModel(
    private val groupId: GroupId,
    private val groups: GroupRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(GroupInviteUiState())
    val state: StateFlow<GroupInviteUiState> = _state.asStateFlow()

    private var loading = false

    init {
        refresh()
    }

    /**
     * Reads the GROUP. Never the link.
     *
     * Retry lands here too, which is why it is safe to press repeatedly: nothing on this path
     * mutates anything. The link on screen is deliberately left alone -- it is still the link this
     * session was handed, and a failed group read says nothing about it.
     */
    fun refresh() {
        if (loading) return
        loading = true
        _state.value = _state.value.copy(error = null)
        viewModelScope.launch {
            try {
                _state.value = _state.value.copy(
                    group = groups.group(groupId),
                    loadedOnce = true,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = error.groupBanner("Could not load this group."),
                )
            } finally {
                loading = false
            }
        }
    }

    /**
     * `createOrRotateInviteLink`. The one call this screen makes, and only from an explicit tap.
     *
     * THE DISPLAYED LINK IS CLEARED FIRST, before the request is even sent. That is the rule the
     * whole screen turns on: from the moment a replacement is asked for, the token on screen is one
     * the user may already have destroyed, and leaving it up for the length of a round trip is
     * leaving a credential on screen that may no longer be a credential. It is not restored on
     * failure either -- see [GroupInviteUiState.uncertain].
     *
     * The permission is re-checked here rather than trusted from the button. `canInviteMembers` is
     * read off a group that may be a minute old, and a demotion in that minute would otherwise send
     * a request that can only 403.
     */
    fun createOrRotate() {
        val current = _state.value
        if (current.busy) return
        // NONE covers both "not loaded" and "not allowed", which is exactly the set of states in
        // which no tap should reach the server.
        if (current.action == InviteLinkAction.NONE) return

        _state.value = current.copy(busy = true, error = null, uncertain = false, link = null)
        viewModelScope.launch {
            try {
                val link = groups.createOrRotateInviteLink(groupId)
                _state.value = _state.value.copy(busy = false, link = link)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                val failure = error.asGroupFailure()
                _state.value = _state.value.copy(
                    busy = false,
                    // A refusal the server made a decision about means nothing was written, so the
                    // group's link is whatever it was. Anything else -- a timeout, a dropped
                    // connection, a 5xx -- may have committed a token this client never received.
                    uncertain = failure == GroupFailure.OTHER,
                    error = when (failure) {
                        GroupFailure.INSUFFICIENT_ROLE ->
                            "Only this group's owner and admins can change the invite link."
                        GroupFailure.RATE_LIMITED ->
                            "Too many invite links recently. Try again in a little while."
                        else -> error.groupBanner("Could not get an invite link.")
                    },
                )
                // The permissions this screen was drawn from are evidently not the server's, and
                // the banner above stays up while that is re-read.
                if (failure == GroupFailure.INSUFFICIENT_ROLE) refreshGroupQuietly()
            }
        }
    }

    /** Re-reads the group without touching the banner, after a refusal contradicted it. */
    private fun refreshGroupQuietly() {
        viewModelScope.launch {
            runCatching { groups.group(groupId) }
                .onSuccess { _state.value = _state.value.copy(group = it) }
        }
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}
